import { apiClient } from './client';
import type { ApiResponse } from '@/types/api.types';

export interface FinancialCredential {
  id: string;
  baseUrl: string;
  username: string;
  createdAt: string;
  updatedAt: string;
}

export interface TestAuthResult {
  userId: string;
  userEmail: string;
  userNicename: string;
  userDisplayName: string;
  userRole: string;
}

export interface ScanConfig {
  tenantId: string;
  scanHour: string;
  isActive: boolean;
  lastScanAt: string | null;
}

export interface BoletoItem {
  nomeDocumento?: string;
  urlDownload?: string;
  [key: string]: unknown;
}

export interface RawLinkDoc {
  acessoNfse?: string;
  boletim?: { nomeDocumento?: string; urlDownload?: string } | null;
  boletos?: BoletoItem[];
  [key: string]: unknown;
}

export interface PendenciaMes {
  id: string;
  mes: number;
  ano: number;
  nfseUrl: string | null;
  boletimUrl: string | null;
  boletos: BoletoItem[] | null;
  rawLinks: RawLinkDoc[] | null;
  notifiedAt: string | null;
}

export interface Pendencia {
  id: string;
  documento: string;
  firstSeenAt: string;
  lastSeenAt: string;
  meses: PendenciaMes[];
}

export interface TestDispatchResult {
  documento: string;
  mes: number;
  ano: number;
  boletoUrl: string;
  phone: string;
  zenviaMessageId: string | null;
}

export type ZenviaPlano = 'starter_specialist' | 'expert' | 'professional';

export interface DispatchConfig {
  tenantId: string;
  templateId: string | null;
  credentialId: string | null;
  testPhone: string | null;
  isActive: boolean;
  daysDelay: number;
  maxDaysOverdue: number;
  dispatchHour: string;
  zenviaPlano: ZenviaPlano;
  fieldMapping: Record<string, string> | null;
}

export interface DispatchLogEntry {
  id: string;
  boletoTrackingId: string;
  templateId: string | null;
  credentialId: string | null;
  documento: string;
  mes: number;
  ano: number;
  phone: string;
  boletoUrl: string;
  status: string;
  zenviaMessageId: string | null;
  errorMessage: string | null;
  dispatchedAt: string;
}

export interface DispatchEventEntry {
  id: number;
  uplogLogId: string;
  eventType: string;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

export const uplogApi = {
  getStatus: () => apiClient.get<ApiResponse<{ enabled: boolean }>>('/uplog/status'),

  getCredential: () => apiClient.get<ApiResponse<FinancialCredential>>('/uplog/credentials'),

  upsertCredential: (data: { baseUrl: string; username: string; password: string }) =>
    apiClient.put<ApiResponse<FinancialCredential>>('/uplog/credentials', data),

  testAuth: () => apiClient.post<ApiResponse<TestAuthResult>>('/uplog/credentials/test-auth'),

  getScanConfig: () => apiClient.get<ApiResponse<ScanConfig>>('/uplog/scan-config'),

  updateScanConfig: (data: { scanHour?: string; isActive?: boolean }) =>
    apiClient.patch<ApiResponse<ScanConfig>>('/uplog/scan-config', data),

  triggerScan: () => apiClient.post<ApiResponse<{ message: string }>>('/uplog/scan'),

  getDispatchConfig: () => apiClient.get<ApiResponse<DispatchConfig>>('/uplog/dispatch-config'),

  updateDispatchConfig: (data: Partial<Omit<DispatchConfig, 'tenantId'>>) =>
    apiClient.patch<ApiResponse<DispatchConfig>>('/uplog/dispatch-config', data),

  getDispatchLog: (params?: { page?: number; pageSize?: number; search?: string }) =>
    apiClient.get<{
      data: DispatchLogEntry[];
      meta: { total: number; page: number; pageSize: number; totalPages: number };
    }>('/uplog/dispatch-log', { params }),

  getPendencias: (params?: { page?: number; pageSize?: number; search?: string }) =>
    apiClient.get<{
      data: Pendencia[];
      meta: { total: number; page: number; pageSize: number; totalPages: number };
    }>('/uplog/pendencias', { params }),

  testDispatch: (documento?: string) =>
    apiClient.post<ApiResponse<TestDispatchResult>>(
      '/uplog/dispatch-test',
      documento ? { documento } : {},
    ),

  getDispatchEvents: (id: string) =>
    apiClient.get<{ data: DispatchEventEntry[] }>(`/uplog/dispatch-log/${id}/events`),

  getDashboardStats: (period?: string) =>
    apiClient.get<{
      data: {
        period: string;
        dispatches: {
          total: number;
          pending: number;
          sent: number;
          delivered: number;
          read: number;
          failed: number;
        };
        pending: { total: number; vencidos: number };
        timeSeries: {
          day: string;
          pending: number;
          sent: number;
          delivered: number;
          read: number;
          failed: number;
        }[];
      };
    }>('/uplog/dashboard-stats', { params: { period } }),
};
